/*
 * <newsletter-form> sends the footer's newsletter signup (snippets/newsletter.liquid) without leaving
 * the page. Shopify answers the form with a redirect back to the page, and a full reload was slow and
 * left phones scrolled wherever the form's anchor landed; here the same request goes by fetch, and
 * the `newsletter` region of the page Shopify answers with (the thank-you message, or the error with
 * the field) replaces the form's. The button dims from the press until the answer.
 *
 * Shopify's spam protection (hCaptcha, assets from Shopify) takes the form over on first focus: it
 * cancels the submit, gets its token, and then calls the form's submit(), which it reads when it
 * takes the form over. That method is this element's send, so the token goes along in the request.
 * The document's submit listener runs after the captcha's own, so it sends only a submit nobody took
 * over. A response without the region (Shopify's challenge page) or a failed request falls back to
 * the native submit, which Shopify handles as before.
 */

class NewsletterForm extends HTMLElement {
  #abort;
  #idle;
  #sending = false;

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form || !window.themeRegions) return;

    this.form.submit = () => this.#send();
    this.#abort = new AbortController();
    document.addEventListener('submit', this.#onSubmit, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#idle);
  }

  #onSubmit = (event) => {
    if (event.target !== this.form) return;
    this.#busy(true);
    if (!event.defaultPrevented) {
      event.preventDefault();
      this.#send();
      return;
    }
    // Taken over by the captcha: if it never sends (a challenge closed unsolved), the button comes back.
    clearTimeout(this.#idle);
    this.#idle = setTimeout(() => {
      if (!this.#sending) this.#busy(false);
    }, 15000);
  };

  async #send() {
    if (this.#sending) return;
    this.#sending = true;
    this.#busy(true);
    try {
      const regions = await window.themeRegions.load(['newsletter'], {
        url: this.form.action.split('#')[0],
        method: 'POST',
        body: new FormData(this.form),
      });
      if (!regions.size) throw new Error('No newsletter region in the answer');
      window.themeRegions.apply(regions);
    } catch {
      HTMLFormElement.prototype.submit.call(this.form);
      return;
    } finally {
      this.#sending = false;
    }
    this.#busy(false);
  }

  #busy(on) {
    if (on) this.form.setAttribute('aria-busy', 'true');
    else this.form.removeAttribute('aria-busy');
  }
}

if (!customElements.get('newsletter-form')) customElements.define('newsletter-form', NewsletterForm);
